import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc as firestoreDoc, deleteField, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../../firebase';
import './FilesCabinet.css';
import UploadBankStatements from './UploadBankStatements';

function FilesCabinet() {
  const [expandedSections, setExpandedSections] = useState(['bank-statements']); // Start with bank statements expanded
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const user = auth.currentUser;

  const sections = [
    { id: 'bank-statements', label: 'Bank Statements', icon: '🏦' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'receipts', label: 'Receipts', icon: '🧾' },
    { id: 'documents', label: 'Documents', icon: '📄' },
    { id: 'other', label: 'Other', icon: '📎' }
  ];

  // Toggle section expand/collapse
  const toggleSection = (sectionId) => {
    if (expandedSections.includes(sectionId)) {
      setExpandedSections(expandedSections.filter(id => id !== sectionId));
    } else {
      setExpandedSections([...expandedSections, sectionId]);
      // Load files if not already loaded
      if (!files[sectionId]) {
        loadFilesForSection(sectionId);
      }
    }
  };

  // Load files for a specific section
  const loadFilesForSection = async (sectionId) => {
    if (!user) return;
    
    setLoading(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      let categoryKey;
      let loadedFiles = [];

      // Map section ID to category key
      if (sectionId === 'bank-statements') {
        categoryKey = 'bank_statements';
      } else if (sectionId === 'reports') {
        categoryKey = 'reports';
      } else {
        categoryKey = sectionId.replace('-', '_');
      }

      console.log('📂 Loading files for:', sectionId, 'categoryKey:', categoryKey);

      // Load files based on structure
      if (sectionId === 'reports') {
        // Reports structure: users/{uid}/cabinet/files/reports/{monthKey}
        // Each monthKey document contains multiple reports as fields
        const reportsCollectionRef = collection(db, 'users', user.uid, 'cabinet', 'files', 'reports');
        const reportsSnapshot = await getDocs(reportsCollectionRef);
        
        reportsSnapshot.forEach(monthDoc => {
          const monthKey = monthDoc.id;
          const monthData = monthDoc.data();
          
          if (monthData && Object.keys(monthData).length > 0) {
            // Each field in the document is a report
            Object.entries(monthData).forEach(([fileKey, fileData]) => {
              if (typeof fileData === 'object' && fileData.fileName && fileData.storagePath) {
                loadedFiles.push({
                  id: fileKey,
                  fileName: fileData.fileName,
                  storagePath: fileData.storagePath,
                  uploadedAt: fileData.uploadedAt,
                  monthKey: monthKey,
                  category: 'reports',
                  accountName: null,
                  transactionCount: fileData.transactionCount || null,
                  uploadDate: fileData.uploadDate || null
                });
              }
            });
          }
        });
      } else {
        // Standard structure for other categories
        const pathParts = categoryKey.split('/');
        let filesRef;
        
        if (pathParts.length > 1) {
          filesRef = collection(db, 'users', user.uid, 'cabinet', 'files', ...pathParts);
        } else {
          filesRef = collection(db, 'users', user.uid, 'cabinet', 'files', categoryKey);
        }
        
        const snapshot = await getDocs(filesRef);
        
        snapshot.forEach(monthDoc => {
          const monthKey = monthDoc.id;
          const monthData = monthDoc.data();
          
          if (monthData && Object.keys(monthData).length > 0) {
            Object.entries(monthData).forEach(([key, value]) => {
              if (typeof value === 'object' && value.fileName) {
                loadedFiles.push({
                  id: key,
                  fileName: value.fileName,
                  storagePath: value.storagePath,
                  uploadedAt: value.uploadedAt,
                  monthKey: monthKey,
                  category: categoryKey,
                  accountName: categoryKey === 'bank_statements' ? key : null,
                  transactionCount: value.transactionCount || null
                });
              }
            });
          }
        });
      }

      // Sort by month descending
      loadedFiles.sort((a, b) => b.monthKey.localeCompare(a.monthKey));
      
      console.log('✅ Loaded files:', loadedFiles.length);
      setFiles(prev => ({ ...prev, [sectionId]: loadedFiles }));
    } catch (error) {
      console.error('Error loading files:', error);
      setFiles(prev => ({ ...prev, [sectionId]: [] }));
    } finally {
      setLoading(prev => ({ ...prev, [sectionId]: false }));
    }
  };

  // Load initial sections on mount
  useEffect(() => {
    expandedSections.forEach(sectionId => {
      if (!files[sectionId]) {
        loadFilesForSection(sectionId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadSuccess = () => {
    // Reload all expanded sections after successful upload
    expandedSections.forEach(sectionId => {
      loadFilesForSection(sectionId);
    });
  };

  const handleCreateFile = (sectionId) => {
    alert(`Create file in ${sectionId}`);
  };

  const handleDeleteFile = async (file, sectionId) => {
    if (!window.confirm(`Delete ${file.fileName}?`)) return;
    
    try {
      // Delete from Storage
      const storageRef = ref(storage, file.storagePath);
      await deleteObject(storageRef);
      
      // Delete from Firestore
      if (sectionId === 'reports') {
        // For reports: users/{uid}/cabinet/files/reports/{monthKey}
        // Delete the specific field (fileKey) from the month document
        const monthDocRef = firestoreDoc(
          db, 
          'users', 
          user.uid, 
          'cabinet', 
          'files', 
          'reports',
          file.monthKey
        );
        await updateDoc(monthDocRef, {
          [file.id]: deleteField()
        });
      } else {
        const categoryPath = file.category.split('/');
        const monthDocRef = firestoreDoc(db, 'users', user.uid, 'cabinet', 'files', ...categoryPath, file.monthKey);
        await updateDoc(monthDocRef, {
          [file.id]: deleteField()
        });
      }
      
      // Reload files for this section
      loadFilesForSection(sectionId);
      alert('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file: ' + error.message);
    }
  };

  const handleViewFile = async (file) => {
    try {
      const storageRef = ref(storage, file.storagePath);
      const url = await getDownloadURL(storageRef);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Failed to view file: ' + error.message);
    }
  };

  const formatMonthDisplay = (monthKey) => {
    try {
      const [year, month] = monthKey.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return monthKey;
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toUpperCase();
    if (ext.includes('PDF')) return { text: 'PDF', color: '#ff7961' };
    if (['JPG', 'JPEG', 'PNG'].includes(ext)) return { text: 'IMG', color: '#26a69a' };
    if (['XLS', 'XLSX'].includes(ext)) return { text: 'XLS', color: '#4caf50' };
    if (['DOC', 'DOCX'].includes(ext)) return { text: 'DOC', color: '#2196f3' };
    return { text: 'FILE', color: '#607d8b' };
  };

  // Filter files based on search
  const getFilteredFiles = (sectionFiles) => {
    if (!sectionFiles) return [];
    return sectionFiles.filter(file => 
      file.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (file.accountName && file.accountName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  // Count total files
  const getTotalFiles = () => {
    return Object.values(files).reduce((total, sectionFiles) => {
      return total + (sectionFiles?.length || 0);
    }, 0);
  };

  return (
    <div className="files-cabinet-tree">
      <div className="section-header">
        <h2>🗄️ Files Cabinet</h2>
        <div className="header-actions">
          <input
            type="text"
            className="search-input"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="file-count">{getTotalFiles()} files</span>
          <button 
            className="btn-header-action"
            onClick={() => handleCreateFile('reports')}
            title="Create Report"
          >
            Create
          </button>
          <button 
            className="btn-header-action btn-primary"
            onClick={() => setUploadModalOpen(true)}
            title="Add File"
          >
            Add File
          </button>
        </div>
      </div>

      <div className="tree-container">{sections.map(section => {
          const isExpanded = expandedSections.includes(section.id);
          const sectionFiles = getFilteredFiles(files[section.id]);
          const isLoading = loading[section.id];

          return (
            <div key={section.id} className="tree-section">
              {/* Main Section Header */}
              <div 
                className="tree-section-header"
                onClick={() => toggleSection(section.id)}
              >
                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                <span className="section-icon">{section.icon}</span>
                <span className="section-label">{section.label}</span>
                <span className="section-count">
                  {files[section.id] ? `(${files[section.id].length})` : ''}
                </span>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="tree-section-content">
                  {/* Files List */}
                  <div className="tree-files-list">
                    {isLoading ? (
                      <div className="tree-loading">Loading...</div>
                    ) : sectionFiles.length === 0 ? (
                      <div className="tree-no-files">No files uploaded yet</div>
                    ) : (
                      <MonthGroupedFilesList 
                        files={sectionFiles}
                        onView={handleViewFile}
                        onDelete={(file) => handleDeleteFile(file, section.id)}
                        getFileIcon={getFileIcon}
                        formatMonthDisplay={formatMonthDisplay}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Upload Modal */}
      <UploadBankStatements 
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
}

// Month Grouped Files List Component
function MonthGroupedFilesList({ files, onView, onDelete, getFileIcon, formatMonthDisplay }) {
  const [expandedMonths, setExpandedMonths] = useState([]);

  // Group files by month
  const filesByMonth = files.reduce((acc, file) => {
    const monthKey = file.monthKey || 'unknown';
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(file);
    return acc;
  }, {});

  // Sort months in descending order
  const sortedMonths = Object.keys(filesByMonth).sort((a, b) => b.localeCompare(a));
  const sortedMonthsKey = sortedMonths.join(',');

  // Auto-expand the most recent month on first render
  useEffect(() => {
    if (sortedMonths.length > 0 && expandedMonths.length === 0) {
      setExpandedMonths([sortedMonths[0]]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMonthsKey]);;

  const toggleMonth = (monthKey) => {
    if (expandedMonths.includes(monthKey)) {
      setExpandedMonths(expandedMonths.filter(m => m !== monthKey));
    } else {
      setExpandedMonths([...expandedMonths, monthKey]);
    }
  };

  return (
    <>
      {sortedMonths.map(monthKey => {
        const isExpanded = expandedMonths.includes(monthKey);
        
        return (
          <div key={monthKey} className="month-group">
            <div 
              className="month-group-header"
              onClick={() => toggleMonth(monthKey)}
            >
              <span className="expand-icon-small">{isExpanded ? '▼' : '▶'}</span>
              <span className="month-dot"></span>
              <span className="month-label">{formatMonthDisplay(monthKey)}</span>
              <span className="month-count">({filesByMonth[monthKey].length})</span>
            </div>
            {isExpanded && (
              <div className="month-group-files">
                {filesByMonth[monthKey].map(file => (
                  <FileListItem 
                    key={file.id} 
                    file={file}
                    onView={onView}
                    onDelete={() => onDelete(file)}
                    getFileIcon={getFileIcon}
                    formatMonthDisplay={formatMonthDisplay}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// File List Item Component
function FileListItem({ file, onView, onDelete, getFileIcon, formatMonthDisplay }) {
  const icon = getFileIcon(file.fileName);
  
  return (
    <div className="file-list-item">
      <div className="file-list-icon" style={{ backgroundColor: icon.color }}>
        {icon.text}
      </div>
      <div className="file-list-info">
        <div className="file-list-name">{file.fileName}</div>
        <div className="file-list-meta">
          {file.accountName && <span className="file-account-tag">{file.accountName}</span>}
          {file.monthKey && <span className="file-date">{formatMonthDisplay(file.monthKey)}</span>}
          {file.transactionCount && <span className="file-txn-count">{file.transactionCount} txns</span>}
        </div>
      </div>
      <div className="file-list-actions">
        <button className="btn-view-small" onClick={() => onView(file)}>View</button>
        <button className="btn-delete-small" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

export default FilesCabinet;